import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { TextInput, Textarea, Select, Switch, Button, Title, Stack, Group } from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import ImageUpload from '@/components/ImageUpload'
import { api } from '@/lib/api'
import { formatName } from '@/lib/format'

const specialties = ['Гүн ухаан', 'Бясалгал', 'Судар', 'Ритуал']

export default function TeacherForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [users, setUsers] = useState<{ value: string; label: string }[]>([])
  const [avatar, setAvatar] = useState('')
  const [linkedUserId, setLinkedUserId] = useState('')

  const form = useForm({
    initialValues: {
      userId: '',
      name: '',
      title: '',
      specialty: 'Гүн ухаан',
      description: '',
      isFeatured: false,
    },
  })

  useEffect(() => {
    api.admin.getUsers({ limit: '100', hasNoTeacher: 'true' }).then((res) => {
      setUsers(
        res.data.map((u) => ({
          value: u._id as string,
          label: `${formatName(u.fullName as string)} (${u.email as string})`,
        }))
      )
    })
  }, [])

  useEffect(() => {
    if (id) {
      api.admin.getTeachers({ limit: '100' }).then((res) => {
        const teacher = res.data.find((t) => (t._id as string) === id)
        if (teacher) {
          const userObj = teacher.userId as Record<string, unknown> | undefined
          const userId = typeof teacher.userId === 'object' ? (userObj?._id as string) : (teacher.userId as string)
          form.setValues({
            userId: userId || '',
            name: (teacher.name as string) || '',
            title: (teacher.title as string) || '',
            specialty: (teacher.specialty as string) || 'Гүн ухаан',
            description: (teacher.description as string) || '',
            isFeatured: (teacher.isFeatured as boolean) || false,
          })
          setLinkedUserId(userId || '')
          if (userObj?.avatar) setAvatar(userObj.avatar as string)
          if (userId && userObj) {
            const currentUserOption = {
              value: userId,
              label: `${formatName(userObj.fullName as string)} (${userObj.email as string})`,
            }
            setUsers((prev) => {
              if (prev.some((u) => u.value === userId)) return prev
              return [currentUserOption, ...prev]
            })
          }
        }
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  const handleSubmit = async (values: typeof form.values) => {
    setLoading(true)
    try {
      if (id) {
        const { userId: _uid, ...teacherData } = values
        await api.admin.updateTeacher(id, teacherData)
        if (linkedUserId) {
          await api.admin.updateUser(linkedUserId, { avatar })
        }
        notifications.show({ title: 'Амжилттай', message: 'Багш шинэчлэгдлээ', color: 'green' })
      } else {
        await api.admin.createTeacher(values)
        if (values.userId) {
          await api.admin.updateUser(values.userId, { avatar })
        }
        notifications.show({ title: 'Амжилттай', message: 'Багш үүсгэгдлээ', color: 'green' })
      }
      navigate('/teachers')
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Алдаа гарлаа'
      notifications.show({ title: 'Алдаа', message, color: 'red' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div>
      <Title order={2} mb="lg">{id ? 'Багш засах' : 'Шинэ багш'}</Title>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack maw={600}>
          <div>
            <label style={{ fontSize: 14, fontWeight: 500, marginBottom: 4, display: 'block' }}>Зураг</label>
            <ImageUpload value={avatar} onChange={setAvatar} />
          </div>
          <Select label="Хэрэглэгч" data={users} required disabled={!!id} {...form.getInputProps('userId')} />
          <TextInput label="Нэр" {...form.getInputProps('name')} />
          <TextInput label="Цол" required {...form.getInputProps('title')} />
          <Select label="Чиглэл" data={specialties} {...form.getInputProps('specialty')} />
          <Textarea label="Тайлбар" autosize minRows={4} {...form.getInputProps('description')} />
          <Switch label="Онцлох" {...form.getInputProps('isFeatured', { type: 'checkbox' })} />
          <Group>
            <Button type="submit" loading={loading}>{id ? 'Хадгалах' : 'Үүсгэх'}</Button>
            <Button variant="default" onClick={() => navigate('/teachers')}>Буцах</Button>
          </Group>
        </Stack>
      </form>
    </div>
  )
}
